import React from 'react';

import TextWithIcon from './text/TextWithIcon';
import TextWithIconDetailed from './text/text_icon_detailed';
import TextWithLogo from './text_with_logo';
import Card from '../fragments/card';
import { graphql } from 'gatsby';
import { cn } from '~/utils/helpers';
import { STYLE_2024 } from '~/utils/constants';

interface ColumnsProps {
  block: {
    _template: string;
    settings: Settings;
    cols: string;
    column_colors: ColumnColors;
    column_background: string;
    style: string;
    icon_size: string;
    show_arrows: boolean;
    special_arrows: string;
    show_numbers: boolean;
    blocks: any[];
    // custom fields added (legacy mutation) when block is nested
    css_class_name?: string;
  };
}

const Columns: React.FC<ColumnsProps> = ({ block }) => {
  const itemsNum = block.blocks.length;
  const column_background_class = getColumnBackgroundClass(
    block.column_background
  );
  const div_class = getBlockDivClass(
    block.blocks,
    block.cols,
    block.show_arrows,
    block.special_arrows
  );
  const arrows_class = getArrowsClass(block.show_arrows, block.special_arrows);
  const numbers_class = block.show_numbers ? ' has-numbers' : '';
  const style_class = getStyleClass(block.style);

  const diagonal_cols = true;
  const diagonal_offset = 2;
  const hasCols = typeof block.cols === 'string' && block.cols.length > 0;
  let reusable_cols_class: string | undefined = undefined;
  if (hasCols) {
    reusable_cols_class = `col-lg-${12 / Number.parseInt(block.cols, 10)} col-md-6 col-12`;
  }

  return (
    <section
      className={cn(
        `block ${block._template} ${block.css_class_name ? block.css_class_name : ''} ${column_background_class ? column_background_class : ''} cols-${block.cols ? block.cols : ''} ${arrows_class ? arrows_class : ''} ${numbers_class ? numbers_class : ''} ${style_class ? style_class : ''} ${block.icon_size === 'Medium' ? 'icon-size--medium' : ''} ${itemsNum % 2 === 0 ? 'is-even' : 'is-odd'}`
      )}
    >
      <div className='container'>
        <div className='row'>
          <div className='cols-wr'>
            <div className='row gy-5 justify-center'>
              {block.blocks.map((column: any, index: number) => {
                const responsiveClass = column.hide_on_mobile
                  ? 'is-desktop'
                  : '';
                const colsClass = `cols-twi ${div_class ? div_class : ''} ${responsiveClass}`;

                switch (column._template) {
                  case 'block__text_with_logo':
                    return (
                      <TextWithLogo
                        key={index}
                        text={column.text}
                        logo={column.logo}
                        className={colsClass}
                      />
                    );
                  case 'block__text_with_icon':
                    column.icon_position_class = getIconPositionClass(
                      column.icon_position
                    );

                    if (block.style) {
                      column.style = block.style;
                    }

                    return (
                      <div
                        key={index}
                        className={colsClass}
                      >
                        <TextWithIcon
                          block={column}
                          column_colors={block.column_colors}
                        />
                        {/* Lets hack: CSS background-image to JS image */}
                        {!!arrows_class &&
                          arrows_class === ' has-arrows-pc' &&
                          index === 0 && (
                            <img
                              src='/images/perma-cache-lines.svg'
                              alt='Perma Cache lines'
                              className='perma-cache-lines'
                            />
                          )}
                      </div>
                    );
                  case 'block__text_icon_detailed':
                    return (
                      <div
                        key={index}
                        className={colsClass}
                      >
                        <TextWithIconDetailed block={column} />
                      </div>
                    );
                  case 'block__card':
                    if (reusable_cols_class) {
                      if (!column.settings)
                        column.settings = { css_class_name: '' };
                      column.settings.css_class_name += reusable_cols_class;
                    }
                    let style;
                    if (diagonal_cols) {
                      style = {
                        marginTop: `${(block.blocks.length - index) * diagonal_offset}rem`,
                      };
                    }
                    return (
                      <Card
                        key={index}
                        block={column}
                        style={style}
                      />
                    );
                  default:
                    return null;
                }
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

function getIconPositionClass(icon_position: string) {
  if (icon_position === 'Top Left') {
    return 'icon-top-left';
  } else if (icon_position === 'Left') {
    return 'icon-left';
  } else if (icon_position === 'Bottom') {
    return 'icon-bottom';
  }
  return 'icon-top';
}

function checkIfHasText(blocks: { text?: string; title?: string }[]) {
  if (!blocks) return false;
  return blocks.some(column => column && column.text && column.title);
}

function getArrowsClass(show_arrows: boolean, special_arrows: string) {
  if (show_arrows) {
    if (special_arrows === 'Perma Cache') {
      return ' has-arrows-pc';
    }
    return ' has-arrows';
  }
  return '';
}

function getColumnBackgroundClass(column_background: string) {
  if (column_background === 'White') {
    return ' bg-white';
  } else if (column_background === 'Blue') {
    return ' bg-blue';
  }
  return '';
}

function getStyleClass(style: string) {
  if (style === 'circle-hover') {
    return ' style-circle-hover';
  } else if (style === 'academy') {
    return ' style-academy';
  } else if (style === 'link') {
    return ' style-link';
  } else if (style === STYLE_2024) {
    return ' style-2024-rework';
  }
  return '';
}

function getBlockDivClass(
  blocks: any[],
  cols: string,
  show_arrows: boolean,
  special_arrows: string
) {
  const hasText = checkIfHasText(blocks);

  switch (cols) {
    case '2':
      return hasText ? 'col-lg-6 col-12' : 'col-lg-6';
    case '3':
      if (show_arrows && special_arrows === 'None') {
        return 'col-lg-4 col-md-6';
      } else if (special_arrows === 'Perma Cache') {
        return 'col-4';
      } else if (hasText) {
        return 'col-xl-4';
      }
      return 'col-xl-4 col-6';
    case '5':
      return 'bunny-col-5';
    case '6':
      return 'bunny-col-6';
    case '7':
      return 'bunny-col-7';
    case '4':
    default:
      if (show_arrows && special_arrows === 'None') {
        if (hasText) {
          return 'col-lg-3 col-md-6 col-12';
        }

        return 'col-lg-3 col-6';
      } else if (hasText) {
        return 'col-xl-3 col-lg-6 col-12';
      }
      return 'col-xl-3 col-6';
  }
}

export default Columns;

export const columnsFragment = graphql`
  fragment ColumnsFragment on Block {
    _template
    __typename
    css_class_name
    column_colors {
      active
      hover
      image
    }
    column_background
    cols
    show_arrows
    special_arrows
    style
    icon_size
  }
`;

export const nestedColumnsBlockFragment = graphql`
  fragment NestedColumnsBlockFragment on NestedBlock {
    ... on TextWithIcon {
      __typename
      ...TextWithIconFragment
    }
    ... on TextWithIconDetailed {
      __typename
      ...TextWithIconDetailedFragment
    }
    ... on TextWithLogo {
      __typename
      ...TextWithLogoFragment
    }
    ... on ColumnsCardBlock {
      __typename
      ...ColumnsCardFragment
    }
  }
`;
